import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, InputAdornment, Paper, TextField, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ImageCropUpload from './ImageCropUpload.js';
import UsernamePasswordFields from './UsernamePasswordFields.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';

/**
 * Org-admin settings cards, shown on the "Organization admin" tab:
 *   1. Organization profile — display picture with crop (stored as base64; the backend
 *      auto-generates the small thumbnail used by search results / queue pages).
 *   2. Login & security — self-service username + password for the org admin
 *      (current password required once one is set).
 */
export default function OrgSettingsCards({ orgId }) {
  /* ── profile picture ── */
  const [picture, setPicture] = useState('');
  const [picLoading, setPicLoading] = useState(true);
  const [picSaving, setPicSaving] = useState(false);
  const [picMsg, setPicMsg] = useState(null); // {severity, text}

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const d = await apiCall(ENDPOINTS.ORG_DISPLAY_PICTURE(orgId));
        if (active) setPicture((d && d.display_picture_base64) || '');
      } catch { /* no picture yet */ }
      if (active) setPicLoading(false);
    })();
    return () => { active = false; };
  }, [orgId]);

  const savePicture = async (dataUrl) => {
    setPicSaving(true);
    setPicMsg(null);
    const prev = picture;
    setPicture(dataUrl); // optimistic preview
    try {
      await apiCall(ENDPOINTS.ORG_UPDATE_INFO(orgId), {
        method: 'PATCH',
        body: JSON.stringify({ display_picture_base64: dataUrl }),
      });
      setPicMsg({ severity: 'success', text: 'Profile photo updated. It may take a moment to refresh in search results.' });
    } catch (err) {
      setPicture(prev);
      setPicMsg({ severity: 'error', text: (err && err.message) || 'Could not save the photo. Please try again.' });
    }
    setPicSaving(false);
  };

  /* ── login & security ── */
  const [creds, setCreds] = useState(null); // {username, has_password}
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [credsValid, setCredsValid] = useState(false);
  const [credsSaving, setCredsSaving] = useState(false);
  const [credsMsg, setCredsMsg] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const d = await apiCall(ENDPOINTS.MY_CREDENTIALS);
        if (active && d) {
          setCreds(d);
          setUsername(d.username || '');
        }
      } catch { /* leave empty */ }
    })();
    return () => { active = false; };
  }, []);

  const dirty = creds && (username.trim() !== creds.username || !!password);
  const canSave = !!dirty && credsValid && (!creds?.has_password || !!currentPassword);

  const saveCreds = async () => {
    setCredsSaving(true);
    setCredsMsg(null);
    try {
      const body = {};
      if (username.trim() && username.trim() !== creds.username) body.username = username.trim();
      if (password) body.password = password;
      if (creds?.has_password) body.current_password = currentPassword;
      const d = await apiCall(ENDPOINTS.MY_CREDENTIALS, { method: 'POST', body: JSON.stringify(body) });
      setCreds({ username: d.username, has_password: d.has_password });
      setUsername(d.username);
      setPassword(''); setConfirm(''); setCurrentPassword('');
      localStorage.setItem('username', d.username);
      setCredsMsg({ severity: 'success', text: d.message || 'Credentials updated.' });
    } catch (err) {
      const msg = (err && (err.message || (err.body && err.body.message))) || 'Could not update credentials.';
      setCredsMsg({ severity: 'error', text: msg });
    }
    setCredsSaving(false);
  };

  return (
    <>
      <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Organization profile</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your photo appears next to your business in search results and on your booking page.
        </Typography>
        {picLoading ? (
          <CircularProgress size={22} />
        ) : (
          <ImageCropUpload value={picture} onChange={savePicture} disabled={picSaving} />
        )}
        {picSaving && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Saving…</Typography>}
        {picMsg && <Alert severity={picMsg.severity} sx={{ mt: 1.5, borderRadius: 2 }}>{picMsg.text}</Alert>}
      </Paper>

      <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Login & security</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set a username and password to sign in without a phone code.
          {creds?.has_password ? ' A password is currently set.' : ' No password is set yet.'}
        </Typography>
        {credsMsg && <Alert severity={credsMsg.severity} sx={{ mb: 2, borderRadius: 2 }}>{credsMsg.text}</Alert>}
        {creds == null ? (
          <CircularProgress size={22} />
        ) : (
          <Box sx={{ maxWidth: 420 }}>
            <UsernamePasswordFields
              username={username} setUsername={setUsername}
              password={password} setPassword={setPassword}
              confirm={confirm} setConfirm={setConfirm}
              excludeSelf
              onValidity={setCredsValid}
            />
            {creds.has_password && (
              <TextField
                fullWidth size="small" type="password" label="Current password"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                helperText="Required to change your username or password."
                InputProps={{ startAdornment: (<InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>) }}
                sx={{ mt: 1 }}
              />
            )}
            <Button
              variant="contained" onClick={saveCreds} disabled={!canSave || credsSaving}
              sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
            >
              {credsSaving ? <CircularProgress size={18} color="inherit" /> : 'Save credentials'}
            </Button>
          </Box>
        )}
      </Paper>
    </>
  );
}
